import { createContext, useContext, useEffect, ReactNode } from "react";
import { X } from "lucide-react";
import { fileUrl } from "./chatConfig";
import type { Message, Room, User, Presence } from "./types";

export type StatusKind = "online" | "idle" | "offline";

export interface Ctx {
  me: User; users: User[]; rooms: Room[];
  userById: (id: number) => User | undefined;
  statusOf: (id: number) => StatusKind;
  socketUp: boolean;
  toast: (text: string, ok?: boolean) => void;
  ask: (opts: { title: string; text?: string; confirm?: string; danger?: boolean }) => Promise<boolean>;
  openRoom: (id: number, opts?: { messageId?: number; replyTo?: Message }) => void;
  reloadRooms: () => Promise<void>;
  roomById: (id: number) => Room | undefined;
  startCall: (roomId: number, type: "audio" | "video") => void;
  joinCall: (callId: number, roomId: number, type: "audio" | "video") => void;
  openTask: (id: number) => void;
  newTask: (prefill?: { assignee_id?: number; room_id?: number; title?: string; description?: string }) => void;
  openDirect: (userId: number, replyTo?: Message) => Promise<void>;
  presence: Record<number, Presence>;
}
export const ChatCtx = createContext<Ctx>(null as unknown as Ctx);
export const useChat = () => useContext(ChatCtx);

const COLORS = ["#2f5bea", "#0ca678", "#7048e8", "#e8590c", "#d6336c", "#1098ad", "#5c7cfa", "#f08c00", "#37b24d", "#c2255c"];
export const colorOf = (name = "?") => COLORS[Array.from(name).reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
export const initialsOf = (name = "?") => name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || "").join("") || "?";

export function Avatar({ name, src, size = 40, status, group }: { name: string; src?: string | null; size?: number; status?: StatusKind; group?: boolean }) {
  const url = src ? fileUrl(src) : "";
  return (
    <span className="cx-avatar" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {url
        ? <img src={url} alt={name} onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
        : <span className="cx-avatar-fallback" style={{ background: group ? "var(--brand-soft)" : colorOf(name), color: group ? "var(--brand)" : "#fff" }}>{group ? "#" : initialsOf(name)}</span>}
      {status && <i className={`cx-dot ${status}`} style={{ width: Math.max(9, size * 0.27), height: Math.max(9, size * 0.27) }} />}
    </span>
  );
}

export function Modal({ title, onClose, children, width = 460, footer }: { title: ReactNode; onClose: () => void; children: ReactNode; width?: number; footer?: ReactNode }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="cx-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="cx-modal" style={{ maxWidth: width }} role="dialog" aria-modal="true">
        <div className="cx-modal-head"><h3>{title}</h3><button className="cx-icon-btn" onClick={onClose} aria-label="بستن"><X size={18} /></button></div>
        <div className="cx-modal-body">{children}</div>
        {footer && <div className="cx-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export const Spinner = ({ size = 18 }: { size?: number }) => <span className="cx-spinner" style={{ width: size, height: size }} />;
export const Empty = ({ icon, title, text, action }: { icon?: ReactNode; title: string; text?: string; action?: ReactNode }) => (
  <div className="cx-empty">{icon && <div className="cx-empty-ic">{icon}</div>}<b>{title}</b>{text && <p>{text}</p>}{action}</div>
);
export const Field = ({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) => (
  <label className="cx-field"><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>
);

/** هایلایت منشن‌ها و لینک‌ها داخل متن پیام */
export function RichText({ text, mentions }: { text: string; mentions?: { id: number; name: string }[] }) {
  const names = (mentions || []).map(m => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`(https?://[^\\s]+${names.length ? "|@(?:" + names.map(esc).join("|") + ")" : ""})`, "g");
  const parts = text.split(pattern);
  return <>{parts.map((p, i) => p.startsWith("http") ? <a key={i} href={p} target="_blank" rel="noreferrer noopener" className="cx-link">{p}</a> : p.startsWith("@") && names.includes(p.slice(1)) ? <span key={i} className="cx-mention">{p}</span> : <span key={i}>{p}</span>)}</>;
}
